import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, map, mergeMap, of, share, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { PulsationModels } from '@models/pulsation.model';
import { CameraStatusActions } from '@states/camera-status/camera-status.action-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { Dictionary } from '@ngrx/entity/src/models';
import { LocationStatusActions } from '@states/location-status/location-status.action-types';
import { Edge } from '../../edge/edge.model';
import { selectAllCameras } from '@states/camera/camera.selectors';
import { EdgeCamera } from '../../cameras/camera.model';
import { EdgeStatusActions } from '@states/edge-status/edge-status.action-types';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { CameraActions } from '@states/camera/camera.action-types';
import * as GrantedAccessAction from '@states/granted-access/granted-access.actions';
import * as GrantedAccessSelectors from '@states/granted-access/granted-access.selectors';
import ComponentStatus = PulsationModels.ComponentStatus;
import ComponentStatusDisplay = PulsationModels.ComponentStatusDisplay;

@Injectable()
export class DeviceStatusEffects {

  public setInitialEdgeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EdgeStatusActions.setInitialEdgeStatus),
      withLatestFrom(
        this.store$.pipe(select(EdgeSelectors.selectAllEdges)),
      ),
      mergeMap(([{ edgeStatuses }, allEdges]) => {
          const organizationEdgeStatuses: Dictionary<Edge.V2.EdgePulsation> = Object.fromEntries(edgeStatuses.map(item => [item.edgeId, item]));

          const edgeStatusLookup: Dictionary<Edge.V2.EdgePulsationDisplay> = {};
          allEdges
            .forEach(edge => {
              const isStatusExists = organizationEdgeStatuses[edge.edgeId];
              edgeStatusLookup[edge.edgeId] = {
                edgeId: edge.edgeId,
                edgeStatus: isStatusExists ? PulsationModels.convertComponentStatusToComponentStatusDisplay(isStatusExists?.edgeStatus) : ComponentStatusDisplay.Unhealthy,
                playbackStatus: isStatusExists ? PulsationModels.convertComponentStatusToComponentStatusDisplay(isStatusExists?.playbackStatus) : ComponentStatusDisplay.Unhealthy,
                metadata: isStatusExists?.metadata,
              };
            });
          return of(EdgeStatusActions.setInitialEdgeStatusSuccess({ edgeStatuses: Object.values(edgeStatusLookup) }));
        },
      ),
      share(),
    ),
  );


  public setInitialCameraStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CameraStatusActions.setInitialCameraStatus,
      ),
      withLatestFrom(
        this.store$.pipe(select(selectAllCameras)),
        this.store$.pipe(select(state => state.edgeStatusState)),
      ),
      mergeMap(([{ cameraStatuses }, allOrganizationCameras, edgeStatusStore]) => {
          const organizationCameraStatuses: Dictionary<Edge.V2.CameraPulsation> = Object.fromEntries(cameraStatuses.map(item => [item.cameraId, item]));
          const organizationCameraStatusesDisplay: Dictionary<Edge.V2.CameraPulsationCalculated> = {};
          allOrganizationCameras.forEach(cameraItem => {
            const cameraId = cameraItem?.edgeOnly?.cameraId;
            if (!organizationCameraStatuses[cameraId]) {
              organizationCameraStatusesDisplay[cameraId] = {
                cameraId: cameraId,
                edgeId: cameraItem.edgeId,
                mainStream: ComponentStatus.NotConnected,
                cameraStatus: ComponentStatusDisplay.Unhealthy,
                storage: ComponentStatus.NotConnected,
                smartStorage: ComponentStatus.NotConnected,
                analytics: ComponentStatus.NotConnected,
                subStream: ComponentStatus.NotConnected,
                playbackStatus: null,
                audioEnabled: false,
              };
            } else {
              organizationCameraStatusesDisplay[cameraId] = this.prepareCameraDisplayItem(organizationCameraStatuses[cameraId], edgeStatusStore.entities, cameraItem);
            }
          });

          return of(CameraStatusActions.setInitialCameraStatusSuccess({ cameraStatuses: Object.values(organizationCameraStatusesDisplay) }));
        },
      ),
      share(),
    ),
  );

  /**
   * Guest/shared-link viewers never load the org's camera/edge lists, so setInitialCameraStatus$/
   * setInitialEdgeStatus$ above (which cross-reference those lists) produce nothing for them -
   * that's why ui-camera-status falls back to a false Offline on the shared live-view page.
   * Build the single viewed camera's status directly from the heartbeat rows already fetched
   * for guests (share-api/heartbeat-status) plus the camera doc already present in
   * grantedAccessState, and write the *Success actions directly - they use setAll, and a guest
   * session only ever has this one camera/edge, so this can't clobber anything else.
   */
  public populateGuestCameraStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GrantedAccessAction.getEdgeHeartBeatStatusSuccess),
      withLatestFrom(
        this.store$.pipe(select(GrantedAccessSelectors.selectSharedLiveView)),
        this.store$.pipe(select(GrantedAccessSelectors.selectSelectedLocation)),
      ),
      mergeMap(([{ body }, sharedLiveView, selectedLocation]) => {
          const cameraId = sharedLiveView?.cameraId;
          const edgeId = sharedLiveView?.edgeId;
          if (!cameraId || !edgeId) {
            return EMPTY;
          }

          const rows: { cameraId: string; component: number; status: number; timestamp: number }[] = body ?? [];
          const numberToComponentStatus = (n: number): PulsationModels.ComponentStatus =>
            Object.values(PulsationModels.ComponentStatus)[n] as PulsationModels.ComponentStatus;
          const latestRow = (component: number, forCameraId: string | null) => {
            return rows
              .filter(row => row.component === component && row.cameraId === forCameraId)
              .reduce((latest, row) => (!latest || row.timestamp > latest.timestamp ? row : latest), null);
          };
          const statusFor = (component: number): PulsationModels.ComponentStatus => {
            const row = latestRow(component, cameraId);
            return row ? numberToComponentStatus(row.status) : PulsationModels.ComponentStatus.Unknown;
          };

          const edgeRow = latestRow(PulsationModels.StreamerComponentAsNumber.Edge, null);
          const edgeStatusDisplay: Edge.V2.EdgePulsationDisplay = {
            edgeId,
            edgeStatus: edgeRow
              ? PulsationModels.convertComponentStatusToComponentStatusDisplay(numberToComponentStatus(edgeRow.status))
              : ComponentStatusDisplay.Unhealthy,
            playbackStatus: ComponentStatusDisplay.Unhealthy,
          };

          const cameraDocument = selectedLocation?.edges?.[edgeId]?.cameras?.[cameraId]?.edgeOnly;
          const cameraItem: EdgeCamera.CameraItem = {
            edgeId,
            locationId: sharedLiveView.locationId,
            edgeOnly: { ...cameraDocument, cameraId },
          };

          const cameraPulsation: Edge.V2.CameraPulsation = {
            edgeId,
            cameraId,
            mainStream: statusFor(PulsationModels.StreamerComponentAsNumber.Input),
            storage: statusFor(PulsationModels.StreamerComponentAsNumber.Storage),
            smartStorage: statusFor(PulsationModels.StreamerComponentAsNumber.SmartStorage),
            analytics: statusFor(PulsationModels.StreamerComponentAsNumber.Analytics),
            subStream: statusFor(PulsationModels.StreamerComponentAsNumber.SubStream1),
          };

          const cameraStatusDisplay = this.prepareCameraDisplayItem(cameraPulsation, { [edgeId]: edgeStatusDisplay }, cameraItem);

          return [
            CameraActions.CreateLocationEdgeCameraSuccess({ payload: cameraItem }),
            EdgeStatusActions.setInitialEdgeStatusSuccess({ edgeStatuses: [edgeStatusDisplay] }),
            CameraStatusActions.setInitialCameraStatusSuccess({ cameraStatuses: [cameraStatusDisplay] }),
          ];
        },
      ),
      share(),
    ),
  );

  public calculateLocationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LocationStatusActions.calculateLocationStatus,
        CameraStatusActions.updateCameraStatusFromSocketSuccess,
        EdgeStatusActions.updateEdgeStatusFromSocketSuccess,
      ),
      withLatestFrom(
        this.store$.pipe(select(LocationSelectors.selectLocationEntities)),
        this.store$.pipe(select(state => state.cameraStatusState)),
        this.store$.pipe(select(state => state.edgeStatusState)),
        this.store$.pipe(select(CameraSelectors.selectProperFitting))
          .pipe(
            map(res => {
              const obj = {};
              res?.map(item => {
                obj[item.cameraId] = item;
              });
              return obj;
            })),
      ),
      mergeMap(([, locations, cameraStatusState, edgeStatusState, properFitting]) => {
          const locationIds = Object.keys(locations);
          const locationStatusLookup: Dictionary<Edge.V2.LocationPulsation> = {};

          locationIds.forEach(locationId => {
            const location = locations[locationId];
            const edges = Object.values(location.edges ?? {});

            /**
             * LOCATION IS ONLINE ONLY IF ALL CHILD EDGES AND CAMERAS ONLINE
             */
            const isEdgesAndCamerasAllOnline = edges.every(edge => {
              const cameras = Object.values(edge.cameras ?? {});
              if (!edgeStatusState.entities[edge.edgeId]) {
                return false;
              }
              return edgeStatusState.entities[edge.edgeId]?.edgeStatus === ComponentStatusDisplay.Online && cameras
                .every(camera => {
                  const cameraId = camera?.edgeOnly?.cameraId;
                  if (!cameraStatusState.entities[cameraId]) {
                    return false;
                  }
                  return cameraStatusState.entities[cameraId].cameraStatus === ComponentStatusDisplay.Online;
                });
            });


            if (isEdgesAndCamerasAllOnline) {
              locationStatusLookup[locationId] = {
                locationId: locationId,
                status: ComponentStatusDisplay.Online,
              };
            } else {
              /**
               * LOCATION IS OFFLINE ONLY IF ALL CHILD EDGES AND CAMERAS OFFLINE
               */
              const isEdgesAndCamerasAllOffline = edges.every(edge => {
                const cameras = Object.values(edge.cameras ?? {});
                if (!edgeStatusState.entities[edge.edgeId]) {
                  return false;
                }
                return edgeStatusState.entities[edge.edgeId]?.edgeStatus === ComponentStatusDisplay.Offline && cameras.every(camera => {
                  const cameraId = camera?.edgeOnly?.cameraId;
                  if (!cameraStatusState.entities[cameraId]) {
                    return false;
                  }
                  const edge = edgeStatusState.entities[camera.edgeId];
                  /**
                   * Handle case if edge offline. Force set camera offline.
                   */
                  let realCameraStatus = cameraStatusState.entities[cameraId].cameraStatus;
                  switch (edge?.edgeStatus) {
                    case PulsationModels.ComponentStatusDisplay.Offline:
                      realCameraStatus = PulsationModels.ComponentStatusDisplay.Offline;
                      break;
                    case PulsationModels.ComponentStatusDisplay.Unhealthy:
                      realCameraStatus = PulsationModels.ComponentStatusDisplay.Unhealthy;
                      break;
                  }
                  return realCameraStatus === ComponentStatusDisplay.Offline;
                });
              });
              if (isEdgesAndCamerasAllOffline) {
                locationStatusLookup[locationId] = {
                  locationId: locationId,
                  status: ComponentStatusDisplay.Offline,
                };
              } else {
                locationStatusLookup[locationId] = {
                  locationId: locationId,
                  status: ComponentStatusDisplay.Unhealthy,
                };
              }
            }


          });

          return of(LocationStatusActions.setLocationStatuses({ locationStatuses: Object.values(locationStatusLookup) }));
        },
      ),
      share(),
    ),
  );

  public updateCameraStatusFromSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraStatusActions.updateCameraStatusFromSocket),
      withLatestFrom(
        this.store$.pipe(select(state => state.cameraStatusState)),
        this.store$.pipe(select(state => state.edgeStatusState)),
        this.store$.pipe(select(CameraSelectors.selectAllCamerasEntities)),
      ),
      mergeMap(([{ toUpdate }, cameraStatusState, edgeStatusStore, allCameraEntities]) => {
          const currentCameraStatusItem = { ...cameraStatusState.entities[toUpdate.cameraId] };
          delete currentCameraStatusItem.cameraStatus;

          // Merge metadata properly - combine existing metadata with new metadata
          const mergedMetadata = {
            ...currentCameraStatusItem.metadata,
            ...toUpdate.metadata,
          };

          const cameraStatusToUpdateItem: Edge.V2.CameraPulsation = {
            ...currentCameraStatusItem,
            ...toUpdate,
            metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
          };
          const cameraItem = allCameraEntities[toUpdate.cameraId];
          if (!cameraItem) {
            return of(CameraStatusActions.updateCameraStatusFromSocketSkip());
          }
          const updatedCameraStatusDisplay = this.prepareCameraDisplayItem(cameraStatusToUpdateItem, edgeStatusStore.entities, cameraItem);
          return of(CameraStatusActions.updateCameraStatusFromSocketSuccess({ cameraStatus: updatedCameraStatusDisplay }));
        },
      ),
      share(),
    ),
  );


  public setCameraStatusTimeout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraStatusActions.setCameraStatusTimeout),
      withLatestFrom(
        this.store$.pipe(select(state => state.cameraState)),
      ),
      mergeMap(([{ cameraStatus }, { entities }]) => {
          const originCamera = entities[cameraStatus.cameraId];
          if (originCamera) {
            return of(CameraStatusActions.setCameraStatusTimeoutSuccess({
              cameraStatus: {
                ...cameraStatus,
                edgeId: originCamera.edgeId,
              },
            }));
          } else {
            return EMPTY;
          }

        },
      ),
      share(),
    ),
  );

  public updateEdgeStatusFromSocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EdgeStatusActions.updateEdgeStatusFromSocket),
      withLatestFrom(
        this.store$.pipe(select(state => state.edgeStatusState)),
        this.store$.pipe(select(CameraSelectors.selectAllCamerasEntities)),
      ),
      mergeMap(([{ toUpdate }, edgeStatusStore, allCameraEntities]) => {
          const isExist = edgeStatusStore.entities[toUpdate.edgeId];
          if (isExist) {
            const currentEdgeStatusItem: Edge.V2.EdgePulsationDisplay = { ...edgeStatusStore.entities[toUpdate.edgeId] };

            // Merge metadata properly - combine existing metadata with new metadata
            const mergedMetadata = {
              ...currentEdgeStatusItem.metadata,
              ...toUpdate.metadata,
            };

            const edgeStatusDisplayItem: Edge.V2.EdgePulsationDisplay = {
              ...currentEdgeStatusItem,
              edgeStatus: toUpdate.edgeStatus ? PulsationModels.convertComponentStatusToComponentStatusDisplay(toUpdate.edgeStatus) : currentEdgeStatusItem.edgeStatus,
              playbackStatus: toUpdate.playbackStatus ? PulsationModels.convertComponentStatusToComponentStatusDisplay(toUpdate.playbackStatus) : currentEdgeStatusItem.playbackStatus,
              metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
            };
            return [
              EdgeStatusActions.updateEdgeStatusFromSocketSuccess({ toUpdate: edgeStatusDisplayItem }),
            ];
          }
          return [
            EdgeStatusActions.updateEdgeStatusFromSocketSuccess({
              toUpdate: {
                edgeStatus: toUpdate.edgeStatus ? PulsationModels.convertComponentStatusToComponentStatusDisplay(toUpdate.edgeStatus) : ComponentStatusDisplay.Unhealthy,
                edgeId: toUpdate.edgeId,
                playbackStatus: toUpdate.playbackStatus ? PulsationModels.convertComponentStatusToComponentStatusDisplay(toUpdate.playbackStatus) : null,
                metadata: toUpdate.metadata,
              },
            }),
          ];
        },
      ),
      share(),
    ),
  );

  constructor(private actions$: Actions, private store$: Store<AppState>) {
  }

  private prepareCameraDisplayItem(toUpdate: Edge.V2.CameraPulsation, edgeStatusEntities: Dictionary<Edge.V2.EdgePulsationDisplay>, cameraItem: EdgeCamera.CameraItem) {
    /**
     * The main stream is pulled on demand both when the HQ toggle says so and when
     * analytics reads the substream - see `EdgeCamera.isMainStreamOnDemand`. Either
     * way its heartbeat is expected to be down, so it must not drive the status.
     */
    const isMainStreamOnDemand = EdgeCamera.isMainStreamOnDemand(cameraItem?.edgeOnly);
    const edgeStatusObject = edgeStatusEntities[toUpdate.edgeId];
    const isEdgeOnline = edgeStatusObject?.edgeStatus === ComponentStatusDisplay.Online;
    const playbackStatus = edgeStatusObject?.playbackStatus;
    const mainStreamStatusOnline = toUpdate.mainStream === ComponentStatus.Online || isMainStreamOnDemand;

    const analyticStatusOnline = toUpdate.analytics === ComponentStatus.Online;
    const storageStatusOnline = toUpdate.storage === ComponentStatus.Online || toUpdate.storage === ComponentStatus.NotRecording;

    let statusOnlineMainCondition = mainStreamStatusOnline
      && (analyticStatusOnline || (typeof cameraItem?.edgeOnly?.analyticMode !== 'undefined' && cameraItem?.edgeOnly?.analyticMode !== EdgeCamera.AnalyticModes.Enabled))
      && storageStatusOnline
      && isEdgeOnline;

    /**
     * 18/12/2023 offline -> if stream is offline.
     * 25/03/2024 offline -> if edge is offline
     * all the other combination that today makes you offline should be changed to Unhealthy.
     *
     * An on-demand main stream is skipped here too: it is offline by design, so it
     * can only downgrade such a camera to Unhealthy, never to Offline.
     */
    let statusOfflineMainCondition = (!isMainStreamOnDemand && toUpdate.mainStream === ComponentStatus.Offline)
      || edgeStatusEntities[cameraItem.edgeId]?.edgeStatus === ComponentStatusDisplay.Offline;

    const hasSubstream = !!cameraItem.edgeOnly?.subStreams?.filter(item => !!item?.main)?.length;

    if (hasSubstream && cameraItem.edgeOnly.smartStorage?.enabled && !isMainStreamOnDemand) {
      const smartStorageStatus = toUpdate.smartStorage;
      statusOnlineMainCondition = statusOnlineMainCondition && (smartStorageStatus === PulsationModels.ComponentStatus.Online || smartStorageStatus === ComponentStatus.NotRecording);
    }

    if (hasSubstream && cameraItem.edgeOnly.storageStream?.storageFromSubstream) {
      const subStreamStatus = toUpdate.subStream;
      statusOnlineMainCondition = statusOnlineMainCondition && subStreamStatus === PulsationModels.ComponentStatus.Online;
    }

    const cameraStatus: PulsationModels.ComponentStatusDisplay = statusOnlineMainCondition ? ComponentStatusDisplay.Online : (statusOfflineMainCondition ? ComponentStatusDisplay.Offline : ComponentStatusDisplay.Unhealthy);
    const updatedCameraStatusDisplay: Edge.V2.CameraPulsationCalculated = {
      cameraId: toUpdate.cameraId,
      edgeId: cameraItem.edgeId,
      cameraStatus: cameraStatus,
      mainStream: toUpdate.mainStream,
      storage: toUpdate.storage,
      smartStorage: toUpdate.smartStorage,
      analytics: toUpdate.analytics,
      subStream: toUpdate.subStream,
      audioEnabled: toUpdate.audioEnabled,
      playbackStatus: playbackStatus,
      metadata: toUpdate.metadata,
    };
    return updatedCameraStatusDisplay;
  }
}
